import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { embedLogin, fetchMe, type MeResponse } from '../api/auth';
import { setToken, getToken } from '../api/apiFetch';
import type { SessionUser } from '@deposits/shared';

const IFRAME_TOKEN_KEY = 'deposits.iframe.accessToken';

interface AuthState {
  status: 'loading' | 'authenticated' | 'unauthenticated' | 'error';
  user: SessionUser | null;
  /** Whether this user's Arbox role may see the safe (כספת) tabs. Defaults to
   *  false until /me resolves, so the safe stays hidden while loading. */
  canViewSafe: boolean;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  refetchMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readUrlAccessToken(): string | null {
  // The Arbox parent sometimes appends a trailing analytics param with `?`
  // instead of `&` (e.g. ...&accessToken=<jwt>?dataLayer=first_time), which
  // glues extra text onto the JWT and breaks signature verification. A JWT
  // never contains `?`, so defensively trim at the first one. Treat the param
  // as untrusted/possibly-mangled by the parent — do not assume it is last.
  const raw = new URLSearchParams(window.location.search).get('accessToken');
  return raw ? raw.split('?')[0] ?? null : null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: 'loading',
    user: null,
    canViewSafe: false,
    error: null,
  });

  const refetchMe = useCallback(async () => {
    try {
      const me: MeResponse = await fetchMe();
      setState({
        status: 'authenticated',
        user: me.user,
        canViewSafe: me.can_view_safe,
        error: null,
      });
    } catch (err) {
      setState({
        status: 'unauthenticated',
        user: null,
        canViewSafe: false,
        error: (err as Error).message,
      });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const urlToken = readUrlAccessToken();
      const cachedIframeToken = sessionStorage.getItem(IFRAME_TOKEN_KEY);
      const cachedSession = getToken();

      // User-switch in same tab: Arbox re-mounts iframe with a different
      // accessToken for User B while we still hold User A's session JWT.
      // Compare URL token vs cached and re-handshake if they differ.
      // See docs/platform/04-arbox-iframe-auth.md §9.
      if (urlToken && cachedIframeToken && urlToken !== cachedIframeToken) {
        setToken(null);
        sessionStorage.removeItem(IFRAME_TOKEN_KEY);
      }

      // If we already have a valid-looking session JWT, try /me first.
      if (cachedSession && (!urlToken || urlToken === cachedIframeToken)) {
        try {
          const me: MeResponse = await fetchMe();
          if (!cancelled) {
            setState({
              status: 'authenticated',
              user: me.user,
              canViewSafe: me.can_view_safe,
              error: null,
            });
          }
          return;
        } catch {
          // Session expired or bad — fall through to fresh embed-login if we have a URL token.
          setToken(null);
        }
      }

      if (!urlToken) {
        if (!cancelled) {
          setState({
            status: 'unauthenticated',
            user: null,
            canViewSafe: false,
            error: 'missing accessToken (open this app from inside Arbox)',
          });
        }
        return;
      }

      try {
        const result = await embedLogin(urlToken);
        setToken(result.token);
        sessionStorage.setItem(IFRAME_TOKEN_KEY, urlToken);
        // embed-login returns identity only; /me adds permissions
        // (can_view_safe), so resolve the full session through it.
        const me: MeResponse = await fetchMe();
        if (!cancelled) {
          setState({
            status: 'authenticated',
            user: me.user,
            canViewSafe: me.can_view_safe,
            error: null,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            status: 'error',
            user: null,
            canViewSafe: false,
            error: (err as Error).message,
          });
        }
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => ({ ...state, refetchMe }), [state, refetchMe]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
